import Axios from "axios";
import { Button } from "components";
import {
    BookingFlowNarrowContent,
    BookingFlowPageHeading,
    BookingFlowPaymentFooterDisclaimers,
    BookingFlowScreeningPayment,
} from "components/booking-flow";
import { getPrivateBookingFlowDueSummary } from "utils/booking-flow-payment-due-summary";
import useAnalytics from "hooks/analytics";
import useBookingFlowRedesign from "hooks/useBookingFlowRedesign";
import useAuthUser from "hooks/auth-user";
import { FollowUpType } from "models/appointment";
import SuggestedMedicalTest from "models/suggested-medical-test";
import { useEffect, useState } from "react";
import { useAsyncCallback } from "react-async-hook";
import { trackScreeningPaymentSubmitted } from "utils/analytics/booking-flow-tracking";
import { buildReservedAppointmentDescription } from "utils/appointment-display";
import ENDPOINTS from "utils/endpoints";
import {
    priceList,
    PrivateLabCalculator,
} from "utils/private-lab-calculator/private-lab-calculator";
import { getAppointmentHold } from "utils/session-storage";
import { CardElementsProvider } from "context/stripe-provider";
import useLanguage from "../../../../hooks/language";
import PaymentFieldComponent from "../payment-field/payment-field";
import ExpressWalletCheckout, {
    WalletCheckoutFlow,
} from "../payment-field/express-wallet-checkout";
import PaymentMethodsBusy from "../payment-field/payment-methods-busy";
import useCardPlaceholder from "../payment-field/use-card-placeholder";
import useCardToken from "../payment-field/use-card-token";
import { ScreeningPaymentType } from "../payment-types";
import styles from "./upfront-payments.module.css";

interface UpfrontPaymentsProps {
    onSelectFollowUp: (selectedFollowUp: FollowUpType) => void;
    onCompletePayment: (
        hasValidCreditCard: boolean,
        resolvedFollowUpType: FollowUpType
    ) => Promise<void> | void;
    medicalTests: SuggestedMedicalTest[];
}

type PrivateCardPaymentProps = Omit<UpfrontPaymentsProps, "medicalTests"> & {
    isBusy?: boolean;
};

function PrivateCardPayment({
    onSelectFollowUp,
    onCompletePayment,
    isBusy = false,
}: PrivateCardPaymentProps): JSX.Element {
    const [authUser] = useAuthUser();
    const { translations } = useLanguage();
    const cardInfo = useCardPlaceholder();
    const getCardTokenCallback = useCardToken();
    const [analytics] = useAnalytics();
    const tUpfrontPayments = translations.upfrontPaymentSection;

    const isUserCardAvailable = !cardInfo.loading && !!cardInfo.result;

    const [isCardFieldValid, setIsCardFieldValid] = useState(
        isUserCardAvailable
    );
    useEffect(() => {
        // Update card field validity whenever async cardInfo loads
        // This ensures that the button's disabled state correctly reflects
        // whether a saved card is available after the API response returns
        if (isUserCardAvailable) {
            setIsCardFieldValid(true);
        }
    }, [isUserCardAvailable]);

    const handleSubmitPayment = useAsyncCallback(async () => {
        if (!authUser || !authUser.lastUserQuestionnaire) {
            return;
        }

        const nextFollowUpType = FollowUpType.Express;
        onSelectFollowUp(nextFollowUpType);

        const cardToken = await getCardTokenCallback();
        if (cardToken) {
            await Axios.put(ENDPOINTS.USER_PAYMENT_INFO, {
                token: cardToken,
            });
        }
        if (cardToken || isUserCardAvailable) {
            const method = cardToken
                ? ScreeningPaymentType.CreditCard
                : ScreeningPaymentType.CardOnFile;
            trackScreeningPaymentSubmitted(analytics, method);
            await onCompletePayment(true, nextFollowUpType);
        } else {
            setIsCardFieldValid(false);
        }
    });

    return (
        <>
            <PaymentFieldComponent
                loading={cardInfo.loading}
                setIsValid={setIsCardFieldValid}
                existingCardInfo={cardInfo.loading ? null : cardInfo.result}
            />

            <Button
                loading={handleSubmitPayment.loading || isBusy}
                onClick={() => handleSubmitPayment.execute()}
                disabled={!isCardFieldValid || isBusy}
            >
                {tUpfrontPayments.paymentForm.confirmButtonText}
            </Button>
        </>
    );
}

export default function UpfrontPayments({
    onSelectFollowUp,
    onCompletePayment,
    medicalTests,
}: UpfrontPaymentsProps): JSX.Element {
    const isBookingFlowRedesign = useBookingFlowRedesign();
    const { translations, language } = useLanguage();
    const [analytics] = useAnalytics();
    const tUpfrontPayments = translations.upfrontPaymentSection;
    const appointmentHold = getAppointmentHold();
    const [isWalletProcessing, setIsWalletProcessing] = useState(false);

    const handleWalletPaid = async (method: string) => {
        const nextFollowUpType = FollowUpType.Express;
        onSelectFollowUp(nextFollowUpType);
        trackScreeningPaymentSubmitted(analytics, method);
        await onCompletePayment(true, nextFollowUpType);
    };

    const description =
        buildReservedAppointmentDescription(
            appointmentHold?.datetime,
            appointmentHold?.clinicName,
            language,
            tUpfrontPayments.description
        ) ?? tUpfrontPayments.descriptionFallback;

    const calculator = new PrivateLabCalculator(priceList, medicalTests);
    const total = calculator.total();
    const labFee =
        total === undefined
            ? tUpfrontPayments.private.labFeeAmount
            : language === "fr"
            ? `${total}$`
            : `$${total}`;

    const paymentDetails = (
        <>
            <PaymentMethodsBusy
                busy={isWalletProcessing}
                label={tUpfrontPayments.paymentForm.walletProcessing}
            >
                <ExpressWalletCheckout
                    flow={WalletCheckoutFlow.ExpressPayPrivate}
                    onPaid={handleWalletPaid}
                    onProcessingChange={setIsWalletProcessing}
                />

                <CardElementsProvider>
                    <PrivateCardPayment
                        onSelectFollowUp={onSelectFollowUp}
                        onCompletePayment={onCompletePayment}
                        isBusy={isWalletProcessing}
                    />
                </CardElementsProvider>
            </PaymentMethodsBusy>

            <p
                className={`${styles.upfrontPaymentsSection__text} ${styles["upfrontPaymentsSection__text--bold"]}`}
            >
                {`${tUpfrontPayments.private.labFeeMessage} ${labFee}`}
            </p>

            <BookingFlowPaymentFooterDisclaimers variant="legacy" />
        </>
    );

    if (isBookingFlowRedesign) {
        const dueSummary = getPrivateBookingFlowDueSummary(language, labFee);

        return (
            <BookingFlowNarrowContent className={styles.upfrontPaymentsSection}>
                <BookingFlowPageHeading
                    title={tUpfrontPayments.alternateSectionTitle}
                    subtitle={description}
                    size="large"
                    subtitleFullWidth
                    tightSubtitle
                />
                <BookingFlowScreeningPayment
                    dueSummary={dueSummary}
                    walletCheckoutFlow={WalletCheckoutFlow.ExpressPayPrivate}
                    paymentFollowUpType={FollowUpType.Express}
                    onSelectFollowUp={onSelectFollowUp}
                    onCompletePayment={onCompletePayment}
                />
            </BookingFlowNarrowContent>
        );
    }

    return (
        <div className={`container--narrow ${styles.upfrontPaymentsSection}`}>
            <h2>{tUpfrontPayments.alternateSectionTitle}</h2>

            <div className={styles.upfrontPaymentsSection__service}>
                {description}
            </div>

            <div className={styles.upfrontPaymentsSection__turnaroundTime}>
                {tUpfrontPayments.resultsTurnaroundTimeHeading}
            </div>

            <div className={styles.upfrontPaymentsSection__serviceDetails}>
                <strong>{tUpfrontPayments.private.serviceDelay}</strong>
                <p
                    dangerouslySetInnerHTML={{
                        __html: tUpfrontPayments.private.serviceDelivery,
                    }}
                ></p>
            </div>

            {paymentDetails}
        </div>
    );
}
